import {
    differenceInCalendarMonths,
    format,
    isSameYear,
    isValid,
    parseISO,
} from "date-fns";
import {
    getSourceTypeTemplate,
    HISTORY_FIELD_LABELS,
    HistoryField,
    SCHEDULE_RESOURCE_TYPES,
} from "@common/constants/history-source-types";
import { APIProcessName } from "@common/constants/process-names";
import { currencyFormatterFractionsIfNeeded } from "@common/helpers/currency";
import {
    fullMonthFullYearFormat,
    shortMonthFormat,
} from "@common/helpers/date";
import { ScheduleActivityHistoryPresentation } from "@common/types/auditLog";
import {
    ScheduleChangeJournalEntryMode,
    ScheduleDeleteJournalEntryMode,
    ScheduleStopSettlementKind,
} from "@common/types/domains/accounting/schedule";
import { pluralizeWithCount } from "@common/helpers/string";
import { HistoryRow } from "../views/historyViewConfig";
import { isHistoryField } from "../details/utils/historyDetailsHelpers";
import { ActivityDescriptionParts } from "./activityDescriptionTypes";

const FIELD_TITLED_SOURCE_TYPES: ReadonlySet<string> = new Set([
    APIProcessName.SCHEDULE_TEMPLATE_UPDATE,
    APIProcessName.SCHEDULE_APPLICATION_CHANGE,
]);

type ScheduleActivityJournalEntryMode = NonNullable<
    ScheduleActivityHistoryPresentation["journalEntryMode"]
>;

function journalEntryModeQualifier(
    mode: ScheduleActivityJournalEntryMode,
): string | undefined {
    switch (mode) {
        case ScheduleChangeJournalEntryMode.reverse:
        case ScheduleDeleteJournalEntryMode.reverse:
            return "reversed journal entries";
        case ScheduleChangeJournalEntryMode.remove:
        case ScheduleDeleteJournalEntryMode.remove:
            return "removed journal entries";
        case ScheduleChangeJournalEntryMode.catchUp:
            return undefined;
        default: {
            const unhandled: never = mode;
            return unhandled;
        }
    }
}

const SETTLEMENT_VERBS: Record<ScheduleStopSettlementKind, string> = {
    [ScheduleStopSettlementKind.accelerate]: "recognized",
    [ScheduleStopSettlementKind.reclass]: "reclassified",
};

interface ResolvePrefixParams {
    verb: string;
    field: HistoryField | undefined;
}

export function isFieldTitledScheduleSource(sourceType: string): boolean {
    return FIELD_TITLED_SOURCE_TYPES.has(sourceType);
}

export function getScheduleActivityPresentation(
    row: HistoryRow,
): ScheduleActivityHistoryPresentation | undefined {
    const presentation = row.historyItem.presentation;
    return presentation?.kind === "scheduleActivity" ? presentation : undefined;
}

const parseMonth = (month: string): Date => parseISO(`${month}-01`);

const formatMonth = (month: string): string =>
    format(parseMonth(month), fullMonthFullYearFormat);

function formatCatchUp(catchUpDate: string): string | undefined {
    const month = formatMonths([catchUpDate.slice(0, 7)]);
    return month ? `catch-up in ${month}` : undefined;
}

function areConsecutiveMonths(months: string[]): boolean {
    return months.every(
        (month, index) =>
            index === 0 ||
            differenceInCalendarMonths(
                parseMonth(month),
                parseMonth(months[index - 1]),
            ) === 1,
    );
}

function formatMonths(months: string[]): string | undefined {
    if (!months.every((month) => isValid(parseMonth(month)))) {
        return undefined;
    }
    const [first] = months;
    const last = months[months.length - 1];
    if (!first) {
        return undefined;
    }
    if (first === last) {
        return formatMonth(first);
    }
    const isOneYear = isSameYear(parseMonth(first), parseMonth(last));
    if (!areConsecutiveMonths(months)) {
        return isOneYear
            ? `${months
                  .slice(0, -1)
                  .map((month) => format(parseMonth(month), shortMonthFormat))
                  .join(", ")}, ${formatMonth(last)}`
            : months.map(formatMonth).join(", ");
    }
    const start = isOneYear
        ? format(parseMonth(first), shortMonthFormat)
        : formatMonth(first);
    return `${start}–${formatMonth(last)}`;
}

function resolveQualifiers(
    presentation: ScheduleActivityHistoryPresentation | undefined,
): string[] {
    if (!presentation) {
        return [];
    }
    const { journalEntryMode, catchUpDate, settlement, periodMonths } =
        presentation;
    const journalEntryQualifier = journalEntryMode
        ? journalEntryModeQualifier(journalEntryMode)
        : undefined;
    return [
        journalEntryQualifier,
        catchUpDate ? formatCatchUp(catchUpDate) : undefined,
        settlement
            ? `${SETTLEMENT_VERBS[settlement.kind]} remaining ${currencyFormatterFractionsIfNeeded.format(settlement.amount)}`
            : undefined,
        periodMonths ? formatMonths(periodMonths) : undefined,
    ].filter((qualifier): qualifier is string => !!qualifier);
}

interface SingleChangedFieldParams {
    sourceType: string;
    changedFields: readonly string[];
}

export function resolveSingleChangedField({
    sourceType,
    changedFields,
}: SingleChangedFieldParams): HistoryField | undefined {
    const [field] = changedFields;
    if (
        !isFieldTitledScheduleSource(sourceType) ||
        changedFields.length !== 1 ||
        !isHistoryField(field)
    ) {
        return undefined;
    }
    return field;
}

function resolveSingleField(row: HistoryRow): HistoryField | undefined {
    return resolveSingleChangedField({
        sourceType: row.sourceType,
        changedFields:
            getScheduleActivityPresentation(row)?.changedFields ?? [],
    });
}

function resolvePrefix({ verb, field }: ResolvePrefixParams): string {
    if (!field) {
        return verb;
    }
    if (field === HistoryField.POLICY) {
        return "Replaced policy for";
    }
    return `Updated ${HISTORY_FIELD_LABELS[field]} for`;
}

export function getScheduleActivityDescriptionParts(
    row: HistoryRow,
): ActivityDescriptionParts | undefined {
    if (!SCHEDULE_RESOURCE_TYPES.includes(row.resourceType)) {
        return undefined;
    }
    const template = getSourceTypeTemplate(
        row.sourceType,
        row.resourceType,
        row.fields,
    );
    if (!template) {
        return undefined;
    }
    const qualifiers = resolveQualifiers(getScheduleActivityPresentation(row));
    return {
        prefix: resolvePrefix({
            verb: template.verb,
            field: resolveSingleField(row),
        }),
        counter: pluralizeWithCount(template.noun, row.count),
        suffix: qualifiers.map((qualifier) => `· ${qualifier}`).join("\u00A0"),
    };
}
